import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { updateProperty } from "api/properties";

import { Property } from "js/components/types";

import { queryKeys } from "./queryKeys";

type UpdatePropertyVariables = {
  property: Property;
  propertyId: string;
  queryParams?: object;
};

type UpdatePropertyData = Awaited<ReturnType<typeof updateProperty>>;

export const useUpdateProperty = (
  options?: UseMutationOptions<
    UpdatePropertyData,
    unknown,
    UpdatePropertyVariables
  >,
) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError, onSettled, ...restOptions } = options ?? {};

  return useMutation<UpdatePropertyData, unknown, UpdatePropertyVariables>({
    mutationFn: ({ property, propertyId, queryParams }) =>
      updateProperty(property, propertyId, queryParams),
    ...restOptions,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.properties.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.properties.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.properties.detail(variables.propertyId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.properties.organization(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantReports.lists(),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.landlords.lists() });
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      onError?.(error, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      onSettled?.(data, error, variables, context);
    },
  });
};
